import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@altimateai/lego";
import { Stack, LoadingButton } from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createConnection, updateConnection, testConnection } from "@/helpers/apis";
import { CommonIntegrationInstance, Connection, ConnectionSchema, ConfigVariant } from "./mcpSlice";
import CustomField from "./CustomField";
import { ConnectionsQueryKey, MCPIntegrationType } from "./constants";

interface ConnectionsFormProps {
  integration: CommonIntegrationInstance;
  onSuccess: () => void;
  onCancel: () => void;
  connection?: Partial<Connection>;
}

const ConnectionsForm = ({
  integration,
  onSuccess,
  onCancel,
  connection,
}: ConnectionsFormProps) => {
  const queryClient = useQueryClient();
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>();
  const availableVariants = useMemo<ConfigVariant[]>(
    () => [
      {
        id: "default",
        name: "Default",
        config: integration.config || [],
      },
      ...(integration.additionalConfigVariants || []),
    ],
    [integration.config, integration.additionalConfigVariants]
  ); // Recalc when config changes

  let formSchema = ConnectionSchema.omit({ id: true, created_at: true, updated_at: true });
  if (integration.type === MCPIntegrationType.API) {
    formSchema = formSchema.extend({
      headers: z.array(z.object({ key: z.string(), value: z.string() })).optional(),
    });
  }
  type FormData = z.infer<typeof formSchema>;

  const { register, handleSubmit, reset, formState, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      integration_id: connection?.integration_id || integration.id,
      config: {
        ...(integration.config?.reduce(
          (acc, config) => {
            acc[config.key ?? ""] = config.defaultValue;
            return acc;
          },
          {} as Record<string, unknown>
        ) || {}),
        ...(connection?.config || {}),
      },
    },
  });

  const { mutate: createOrUpdateConnection, isPending: isLoading } = useMutation({
    mutationFn: (data: FormData) => {
      if (connection?.id) {
        return updateConnection(connection.id, { ...data, updated_at: null });
      }
      return createConnection({ ...data, updated_at: null });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ConnectionsQueryKey] });
      reset();
      onSuccess();
    },
  });

  const { mutate: testConnectionMutation, isPending: isTestingConnection } = useMutation({
    mutationFn: (data: FormData) => testConnection(data),
    onSuccess: result => {
      if (result?.success) {
        toast({
          title: "Connection Test Successful",
          description: result?.message ?? "The connection parameters are valid and working.",
        });
      } else {
        toast({
          title: "Connection Test Failed",
          description:
            result?.message ??
            "The connection parameters are invalid or the service is unreachable.",
          variant: "destructive",
        });
      }
    },
    onError: () => {
      toast({
        title: "Connection Test Error",
        description: "An error occurred while testing the connection.",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (connection?.config && connection.config.type) {
      const type = connection.config.type as string;
      const initialVariant = availableVariants.find(v => v.id === type);
      if (initialVariant) {
        setSelectedVariantId(initialVariant.id);
      }
    }
    reset(connection || { integration_id: integration.id, config: {} });
  }, [integration, connection, reset, availableVariants]);

  const onSubmit = (data: FormData) => {
    createOrUpdateConnection(data);
  };

  const onTestConnection = (data: FormData) => {
    testConnectionMutation(data);
  };

  const onVariantChange = (value: string) => {
    const newVariantId = value === "default" ? undefined : value;
    setSelectedVariantId(newVariantId);

    // Merge config values when changing variants
    const newConfig = availableVariants.find(v => v.id === value)?.config || [];
    const mergedConfig = Object.fromEntries(
      newConfig.map(param => [param.key, watch(`config.${param.key}`) || param.defaultValue])
    );
    if (newVariantId) {
      mergedConfig["type"] = newVariantId;
    } else {
      delete mergedConfig["type"];
    }
    setValue("config", mergedConfig);
  };
  return (
    <div className="al-space-y-4">
      <Stack direction="column" className="al-gap-2">
        <label htmlFor="connection-name">
          Name
          <Input id="connection-name" {...register("name")} placeholder="Enter connection name" />
          {formState.errors.name && (
            <p className="al-text-sm al-text-destructive">{formState.errors.name.message}</p>
          )}
        </label>
      </Stack>

      {availableVariants.length > 1 && (
        <Stack direction="column" className="al-gap-2">
          <label htmlFor="type">
            Configuration Type
            <Select
              name={"type"}
              value={selectedVariantId || "default"}
              onValueChange={onVariantChange}
            >
              <SelectTrigger className="al-w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableVariants.map(variant => (
                  <SelectItem key={variant.id} value={variant.id}>
                    {variant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        </Stack>
      )}

      {/* Config fields based on selected variant */}
      {(selectedVariantId
        ? availableVariants.find(v => v.id === selectedVariantId)?.config
        : integration.config || []
      )?.map(config => (
        <CustomField
          key={config.key}
          config={config}
          // @ts-expect-error valid
          register={register}
          formState={formState}
          // @ts-expect-error valid
          watch={watch}
          // @ts-expect-error valid
          setValue={setValue}
          registerPrefix="config."
          fieldPrefix="connection-config-"
        />
      ))}

      {integration.supports_saas_connection_test && (
        <Stack direction="row" className="al-gap-2 al-pt-2">
          <LoadingButton
            type="button"
            variant="success"
            onClick={e => {
              e.stopPropagation();
              handleSubmit(onTestConnection)();
            }}
            loading={isTestingConnection}
            disabled={isLoading}
          >
            Test
          </LoadingButton>
        </Stack>
      )}

      {/* Main Action Buttons */}
      <Stack direction="row" className="al-gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={e => {
            e.stopPropagation();
            reset();
            onCancel();
          }}
          disabled={isLoading || isTestingConnection}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleSubmit(onSubmit)();
          }}
          disabled={isLoading || isTestingConnection}
        >
          {connection?.id ? "Update Connection" : "Save Connection"}
        </Button>
      </Stack>
    </div>
  );
};

export default ConnectionsForm;
